import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export type VoiceStatus = 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'error'

export interface ConversationTurn {
  role: 'user' | 'assistant'
  text: string
}

interface OnboardingVoiceState {
  voiceStatus: VoiceStatus
  transcript: string
  transcriptRole: 'user' | 'assistant'
  /** True once the current turn's transcript is finalized — the next delta starts a fresh line. */
  transcriptFinalized: boolean
  /** Finalized conversation turns in order — both user and assistant. */
  conversationHistory: ConversationTurn[]
  highlightedField: string | null
  sessionId: string | null
  /** Short user-facing notice when the session ends/lost (e.g. "session lost — tap to resume") */
  sessionNotice: string | null
  /** True when noise is detected and the user should be nudged to type instead */
  noiseFallback: boolean
  /** QA-only text-to-SAM chip toggle, pushed once by the backend on the "onboarding:config"
   *  socket event at session start (see OnboardingSessionManager.start()). Not a REST value —
   *  stays false (safe default) until a session has actually started and pushed it. */
  debugTextInputEnabled: boolean
}

const initialState: OnboardingVoiceState = {
  voiceStatus: 'idle',
  transcript: '',
  transcriptRole: 'assistant',
  transcriptFinalized: false,
  conversationHistory: [],
  highlightedField: null,
  sessionId: null,
  sessionNotice: null,
  noiseFallback: false,
  debugTextInputEnabled: false,
}

const onboardingVoiceSlice = createSlice({
  name: 'onboardingVoice',
  initialState,
  reducers: {
    setVoiceStatus(state, action: PayloadAction<VoiceStatus>) {
      state.voiceStatus = action.payload
    },
    setTranscript(
      state,
      action: PayloadAction<{ text: string; role: 'user' | 'assistant'; final?: boolean }>,
    ) {
      const { text, role, final } = action.payload
      // Start a fresh line when the speaker changes, the line is empty, or the previous turn was
      // finalized — otherwise this is the next streaming delta and we APPEND to the growing line.
      const startFresh =
        state.transcriptRole !== role || state.transcript === '' || state.transcriptFinalized

      if (final) {
        // Authoritative end-of-turn text — replace the streamed accumulation and push to history
        // so the UI can render the full conversation rather than just the current turn.
        if (text) {
          state.transcript = text
          state.conversationHistory.push({ role, text })
        }
        state.transcriptRole = role
        state.transcriptFinalized = true
        return
      }

      // Streaming delta — append into a growing line (or start fresh on a new turn).
      state.transcript = startFresh ? text : state.transcript + text
      state.transcriptRole = role
      state.transcriptFinalized = false
    },
    /** Clear the in-progress streaming transcript without adding it to history.
     *  Used when the agent is interrupted mid-speech so the abandoned partial text disappears. */
    clearStreamingTranscript(state) {
      state.transcript = ''
      state.transcriptFinalized = true
    },
    setHighlightedField(state, action: PayloadAction<string | null>) {
      state.highlightedField = action.payload
    },
    setSessionId(state, action: PayloadAction<string | null>) {
      state.sessionId = action.payload
    },
    setSessionNotice(state, action: PayloadAction<string | null>) {
      state.sessionNotice = action.payload
    },
    setDebugTextInputEnabled(state, action: PayloadAction<boolean>) {
      state.debugTextInputEnabled = action.payload
    },
    resetVoice(state) {
      state.voiceStatus = 'idle'
      state.transcript = ''
      state.transcriptFinalized = false
      state.conversationHistory = []
      state.highlightedField = null
      state.sessionId = null
      // sessionNotice intentionally preserved so a "session lost" banner survives the reset;
      // it is cleared explicitly when the user starts a new session.
      // debugTextInputEnabled intentionally preserved — it's a config flag, not per-session
      // voice state, and the next session will just re-push it anyway.
    },
  },
})

export const {
  setVoiceStatus,
  setTranscript,
  clearStreamingTranscript,
  setHighlightedField,
  setSessionId,
  setSessionNotice,
  setDebugTextInputEnabled,
  resetVoice,
} = onboardingVoiceSlice.actions

export default onboardingVoiceSlice.reducer
